/**
 * Web 打印记录出站队列（outbox）
 *
 * 与 App 侧同构的轻量实现：批次一联单打印后调用 createPrintLog；失败时本地 localStorage 落盘，
 * 网络恢复/定时/下次打印时补交。`client_event_id` 保证服务端幂等去重。
 */
import type { CreatePrintLogForm } from '@bayes-mes/shared';

import { printerApi } from '#/api/modules';

interface WebOutboxItem {
  id: string;
  payload: CreatePrintLogForm;
  createdAt: number;
  attempts: number;
  nextRetryAt: number;
  lastError?: string;
}

const STORAGE_KEY = 'mes_print_outbox_v1';
const MAX_ITEMS = 200;
const EXPIRE_MS = 14 * 24 * 60 * 60 * 1000;
const BASE_BACKOFF_MS = 5000;
const MAX_BACKOFF_MS = 10 * 60 * 1000;
const FLUSH_INTERVAL_MS = 60_000;

let flushing = false;
let initialized = false;
let timer: null | ReturnType<typeof setInterval> = null;

function hasStorage(): boolean {
  return typeof window !== 'undefined' && !!window.localStorage;
}

function readQueue(): WebOutboxItem[] {
  if (!hasStorage()) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('[打印记录outbox] 读取失败', error);
    return [];
  }
}

function writeQueue(items: WebOutboxItem[]): boolean {
  if (!hasStorage()) {
    return false;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    return true;
  } catch (error) {
    console.warn('[打印记录outbox] 写入失败（存储可能已满）', error);
    return false;
  }
}

export function generatePrintEventId(): string {
  return `out-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** 错误是否可重试（网络/超时/401/408/429/5xx） */
export function isTransientPrintError(error: any): boolean {
  const status = Number(
    error?.response?.status ?? error?.statusCode ?? error?.status ?? 0,
  );
  if (!status || Number.isNaN(status)) {
    return true;
  }
  if (status === 401 || status === 408 || status === 429) {
    return true;
  }
  return status >= 500;
}

function enqueuePrintLog(payload: CreatePrintLogForm, id: string): void {
  const items = readQueue().filter((item) => item.id !== id);
  items.push({
    id,
    payload,
    createdAt: Date.now(),
    attempts: 0,
    nextRetryAt: 0,
  });
  let bounded = items;
  if (bounded.length > MAX_ITEMS) {
    bounded = bounded.slice(bounded.length - MAX_ITEMS);
  }
  writeQueue(bounded);
}

/** 先直发，可重试失败入队，永久失败只记录 */
export async function sendOrEnqueuePrintLog(
  payload: CreatePrintLogForm,
): Promise<boolean> {
  const id = payload.client_event_id || generatePrintEventId();
  const finalPayload: CreatePrintLogForm = { ...payload, client_event_id: id };
  try {
    await printerApi.createPrintLog(finalPayload);
    return false;
  } catch (error: any) {
    if (!isTransientPrintError(error)) {
      console.error(
        '[打印记录outbox] 永久失败，不入队',
        error?.message || error,
      );
      return false;
    }
    enqueuePrintLog(finalPayload, id);
    return true;
  }
}

function backoffMs(attempts: number): number {
  return Math.min(BASE_BACKOFF_MS * 2 ** Math.max(attempts, 0), MAX_BACKOFF_MS);
}

/** 补交队列；成功/永久失败出队，可重试失败退避保留 */
export async function flushPrintLogOutbox(): Promise<void> {
  if (flushing) {
    return;
  }
  flushing = true;
  try {
    const items = readQueue();
    if (items.length === 0) {
      return;
    }
    const now = Date.now();
    const remaining: WebOutboxItem[] = [];
    let sessionBlocked = false;

    for (const item of items) {
      if (now - item.createdAt > EXPIRE_MS) {
        continue;
      }
      if (item.nextRetryAt > now || sessionBlocked) {
        remaining.push(item);
        continue;
      }
      try {
        await printerApi.createPrintLog(item.payload);
      } catch (error: any) {
        if (isTransientPrintError(error)) {
          const attempts = item.attempts + 1;
          remaining.push({
            ...item,
            attempts,
            nextRetryAt: now + backoffMs(attempts),
            lastError: error?.message || String(error),
          });
          if (Number(error?.response?.status ?? error?.statusCode) === 401) {
            sessionBlocked = true;
          }
        } else {
          console.error(
            '[打印记录outbox] 永久失败，出队',
            item.id,
            error?.message || error,
          );
        }
      }
    }

    const enqueuedDuringFlush = readQueue().filter(
      (queued) => !items.some((item) => item.id === queued.id),
    );
    writeQueue([...enqueuedDuringFlush, ...remaining]);
  } finally {
    flushing = false;
  }
}

export function getPrintLogOutboxPendingCount(): number {
  return readQueue().length;
}

/** 绑定网络恢复/定时补交（幂等） */
export function initPrintLogOutbox(): void {
  if (initialized || typeof window === 'undefined') {
    return;
  }
  initialized = true;
  window.addEventListener('online', () => {
    void flushPrintLogOutbox();
  });
  if (!timer) {
    timer = setInterval(() => {
      void flushPrintLogOutbox();
    }, FLUSH_INTERVAL_MS);
  }
}
